import { useChannelStore, type PendingMessage } from '@/shell/stores/channelStore'
import { dmApi, messageApi } from './api'
import { getSocket } from './socket'

// Central executor for Client-First pending sends. Holds the
// transport-aware logic that used to live as a closure on the pending
// entry — now the closure is gone, so the entry is fully serialisable
// (localStorage survives reload) and we can replay the send on socket
// reconnect / manual retry without re-mounting the original component.

function fireRest(channelKey: string, p: PendingMessage): Promise<void> {
  if (p.transport === 'rest-dm' && p.serverDmChannelId) {
    return dmApi
      .send(p.serverDmChannelId, {
        content: p.content,
        replyToId: p.replyToId,
        audioUrl: p.audioUrl,
        audioDuration: p.audioDuration,
        videoUrl: p.videoUrl,
        videoDuration: p.videoDuration,
        photoUrl: p.photoUrl,
        photoWidth: p.photoWidth,
        photoHeight: p.photoHeight,
        stickerId: p.stickerId,
        gifUrl: p.gifUrl,
        gifWidth: p.gifWidth,
        gifHeight: p.gifHeight,
        attachments: p.attachments,
        // localId is part of the wire schema; the server is idempotent
        // on it, so a retry that races a successful first send returns
        // the existing row instead of duplicating.
        localId: p.localId,
      })
      .then((res) => {
        useChannelStore.getState().confirmPendingMessage(channelKey, p.localId, res.data)
      })
  }
  if (p.transport === 'rest-channel' && p.serverChannelId) {
    return messageApi
      .send({
        channelId: p.serverChannelId,
        content: p.content,
        replyToId: p.replyToId,
        audioUrl: p.audioUrl,
        audioDuration: p.audioDuration,
        videoUrl: p.videoUrl,
        videoDuration: p.videoDuration,
        photoUrl: p.photoUrl,
        photoWidth: p.photoWidth,
        photoHeight: p.photoHeight,
        stickerId: p.stickerId,
        gifUrl: p.gifUrl,
        gifWidth: p.gifWidth,
        gifHeight: p.gifHeight,
        attachments: p.attachments,
        localId: p.localId,
        // OSPROD-66: REST fallback (socket disconnected) must carry the
        // same recipient subset, otherwise a private message silently
        // becomes public when the socket happens to be down.
        ...(p.recipientIds && p.recipientIds.length > 0
          ? { recipientIds: p.recipientIds }
          : {}),
      })
      .then((res) => {
        useChannelStore.getState().confirmPendingMessage(channelKey, p.localId, res.data)
      })
  }
  return Promise.reject(new Error(`Unsupported REST transport: ${p.transport}`))
}

function fireSocket(channelKey: string, p: PendingMessage): void {
  if (!p.serverChannelId) {
    useChannelStore.getState().failPendingMessage(channelKey, p.localId, 'Missing channel id')
    return
  }
  const socket = getSocket()
  if (!socket.connected) {
    // Socket isn't connected — fall back to REST so the user's send
    // doesn't sit in limbo forever. This mirrors the original
    // MessageInput logic but kept centralised here.
    fireRest(channelKey, { ...p, transport: 'rest-channel' }).catch((err) => {
      console.error('REST fallback failed for socket pending:', err)
      useChannelStore.getState().failPendingMessage(channelKey, p.localId, 'Failed to send')
    })
    return
  }
  socket.emit('message:send', {
    channelId: p.serverChannelId,
    content: p.content,
    replyToId: p.replyToId,
    attachments: p.attachments || [],
    stickerId: p.stickerId,
    gifUrl: p.gifUrl,
    gifWidth: p.gifWidth,
    gifHeight: p.gifHeight,
    // OSPROD-66: only attach when the composer's "Кому" picker selected
    // a subset — empty/undefined means "everyone in the call" and the
    // server treats absent recipientIds as a public channel send.
    ...(p.recipientIds && p.recipientIds.length > 0
      ? { recipientIds: p.recipientIds }
      : {}),
    localId: p.localId,
  })
  // No completion ACK — the broadcast confirms via localId. If it never
  // arrives the bubble stays 'sending'; reconnect retry will fire again.
}

export function executePendingSend(channelKey: string, pending: PendingMessage): void {
  if (pending.transport === 'socket-channel') {
    fireSocket(channelKey, pending)
    return
  }
  fireRest(channelKey, pending).catch((err) => {
    console.error('Pending send failed:', err)
    useChannelStore.getState().failPendingMessage(channelKey, pending.localId, 'Failed to send')
  })
}

export function retryPending(channelKey: string, localId: string): void {
  const pending = useChannelStore
    .getState()
    .pendingMessages[channelKey]?.find((p) => p.localId === localId)
  if (!pending) return
  // Flip back to 'sending' so the spinner returns and the user can't
  // double-click Retry mid-flight.
  useChannelStore.getState().markPendingSending(channelKey, localId)
  executePendingSend(channelKey, pending)
}

export function retryAllErroredPending(): void {
  const pendingMap = useChannelStore.getState().pendingMessages
  for (const [channelKey, list] of Object.entries(pendingMap)) {
    for (const p of list) {
      if (p.status === 'error') {
        retryPending(channelKey, p.localId)
      }
    }
  }
}
